import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import Modal from '../../../Components/UI/Modal/Modal';
import {
  acceptContractClient,
  askModificationClient,
  rejectContractClient,
} from '../../../Store/Actions/actionOffer';
import { formatDate } from '../../../Utils/date';

const money = (value) => {
  const number = Number(value);
  return Number.isFinite(number)
    ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(number)
    : 'Non renseigné';
};

const ContractModalClient = ({
  showModal,
  handleClick,
  demand,
  loading,
  acceptContract,
  cancelContract,
  modifyContract,
}) => {
  const history = useHistory();
  const [mode, setMode] = useState('review');
  const [modificationText, setModificationText] = useState('');
  const [submitting, setSubmitting] = useState('');
  const [validationError, setValidationError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedElectronic, setAcceptedElectronic] = useState(false);
  const [typedName, setTypedName] = useState('');

  const opened = showModal === 'open';
  const contract = demand?.contract;
  const modification = demand?.modification;
  const waitingForRevision = demand?.demand_status === 'modification_asked';
  const busy = loading || Boolean(submitting);
  const expectedName = [demand?.user_firstname, demand?.user_lastname].filter(Boolean).join(' ').trim();
  const normalized = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  const signatureReady = acceptedTerms && acceptedElectronic && normalized(typedName) === normalized(expectedName);

  const close = () => {
    if (busy) return;
    setMode('review');
    setModificationText('');
    setValidationError('');
    setAcceptedTerms(false);
    setAcceptedElectronic(false);
    setTypedName('');
    handleClick?.();
  };

  const openArtisan = () => {
    if (!contract?.artisan_id) return;
    close();
    history.push('/artisanboutique', { artisanId: contract.artisan_id });
  };

  const openPrintableContract = () => {
    if (!demand?.demand_id || !contract) return;
    close();
    history.push(`/contract?demandId=${encodeURIComponent(demand.demand_id)}`, { demandToPrint: demand });
  };

  const runDecision = async (decision) => {
    if (!demand || busy) return;

    if (decision === 'modify') {
      const cleanText = modificationText.trim();
      if (cleanText.length < 10) {
        setValidationError('Décrivez la modification souhaitée en au moins 10 caractères.');
        return;
      }
    }

    if (decision === 'accept' && !signatureReady) {
      setValidationError('Confirmez les deux cases et saisissez exactement votre prénom et votre nom.');
      return;
    }

    setValidationError('');
    setSubmitting(decision);

    let success = false;
    if (decision === 'accept') {
      success = await acceptContract(demand, {
        accepted_terms: true,
        accepted_electronic: true,
        typed_name: typedName.trim(),
      });
    }
    if (decision === 'reject') success = await cancelContract(demand);
    if (decision === 'modify') {
      success = await modifyContract({
        demand_id: demand.demand_id,
        modification: modificationText.trim(),
      });
    }

    setSubmitting('');
    if (success) close();
  };

  return (
    <Modal opened={opened} close={close}>
      <Header>
        <div>
          <Eyebrow>Contrat de prestation</Eyebrow>
          <Title>Vérifiez les conditions du chantier</Title>
        </div>
        <CloseButton type="button" aria-label="Fermer" onClick={close}>×</CloseButton>
      </Header>

      {!contract ? (
        <EmptyState>Ce contrat n’est plus disponible.</EmptyState>
      ) : (
        <>
          <ArtisanButton type="button" onClick={openArtisan}>
            <Avatar src={contract.artisan_profile_image || '/images/user.svg'} alt="" />
            <div>
              <strong>
                {[contract.artisan_firstname, contract.artisan_lastname]
                  .filter(Boolean)
                  .join(' ') || 'Artisan'}
              </strong>
              <span>Consulter son profil et ses réalisations</span>
            </div>
          </ArtisanButton>

          <Details>
            <Detail $wide>
              <span>Description du contrat</span>
              <p>{contract.description || 'Aucune description fournie.'}</p>
            </Detail>

            {modification?.modification_reponse && (
              <Detail $wide $highlight>
                <span>Réponse de l’artisan à votre demande</span>
                <p>{modification.modification_reponse}</p>
              </Detail>
            )}

            <Detail>
              <span>Début prévu</span>
              <strong>{formatDate(contract.start_date, 'À convenir')}</strong>
            </Detail>
            <Detail>
              <span>Téléphone de l’artisan</span>
              <strong>{contract.artisan_phone_number || 'Non renseigné'}</strong>
            </Detail>
            <Detail>
              <span>Durée estimée</span>
              <strong>{contract.estimated_duration || 'À préciser'}</strong>
            </Detail>
            <Detail>
              <span>Validité du devis</span>
              <strong>{Number(contract.quote_validity_days || 30)} jours</strong>
            </Detail>
            <Detail>
              <span>Matériaux</span>
              <strong>{money(contract.material_cost)}</strong>
            </Detail>
            <Detail>
              <span>Main-d’œuvre</span>
              <strong>{money(contract.wage)}</strong>
              {contract.labour_mode === 'hours' && Number(contract.labour_hours) > 0 && (
                <small>{Number(contract.labour_hours)} h × {money(contract.hourly_rate)}</small>
              )}
            </Detail>
            {Number(contract.travel_cost) > 0 && (
              <Detail>
                <span>Frais de déplacement</span>
                <strong>{money(contract.travel_cost)}</strong>
              </Detail>
            )}
            {contract.tax_label && (
              <Detail>
                <span>TVA</span>
                <strong>{contract.tax_label}</strong>
              </Detail>
            )}
          </Details>

          {Array.isArray(contract.material_list) && contract.material_list.length > 0 && (
            <MaterialSection>
              <h3>Détail des matériaux</h3>
              <MaterialList>
                {contract.material_list.map((material, index) => {
                  const quantity = Number(material.material_quantity) || 0;
                  const price = Number(material.material_price) || 0;
                  return (
                    <MaterialRow key={`${material.material_name || 'materiel'}-${index}`}>
                      <div>
                        <strong>{material.material_name || 'Matériel'}</strong>
                        <span>{quantity || 1} × {money(price)}</span>
                      </div>
                      <strong>{money((quantity || 1) * price)}</strong>
                    </MaterialRow>
                  );
                })}
              </MaterialList>
            </MaterialSection>
          )}

          <Total>
            <span>Total du contrat</span>
            <strong>{money(contract.total_cost)}</strong>
          </Total>

          <DocumentAccess>
            <div>
              <strong>Document contractuel</strong>
              <span>Ouvrez la version formelle pour l’imprimer ou l’enregistrer en PDF.</span>
            </div>
            <DocumentButton type="button" onClick={openPrintableContract}>Contrat / PDF</DocumentButton>
          </DocumentAccess>

          {waitingForRevision ? (
            <Notice>
              Votre demande de modification a été transmise. Les décisions sont suspendues jusqu’à
              l’envoi d’une nouvelle version par l’artisan.
            </Notice>
          ) : mode === 'modify' ? (
            <ModificationPanel>
              <label htmlFor="contract-modification">Modification demandée</label>
              <textarea
                id="contract-modification"
                value={modificationText}
                onChange={(event) => setModificationText(event.target.value)}
                placeholder="Ex. préciser les matériaux, corriger la date de début ou détailler un poste de coût…"
                maxLength={1000}
               
              />
              <Counter>{modificationText.length}/1000</Counter>
              {validationError && <ErrorMessage role="alert">{validationError}</ErrorMessage>}
            </ModificationPanel>
          ) : (
            <ConsentPanel>
              <ConsentIntro>
                <strong>Acceptation électronique</strong>
                <span>
                  Aucun débit n’est effectué par ce bouton. LePetitBoulot enregistre votre compte authentifié,
                  l’horodatage et l’empreinte SHA-256 des données contractuelles acceptées afin de conserver une preuve de votre accord.
                </span>
              </ConsentIntro>
              <ConsentCheck>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(event) => setAcceptedTerms(event.target.checked)}
                />
                <span>J’ai lu et j’accepte le devis, les prix, le planning et les conditions de paiement.</span>
              </ConsentCheck>
              <ConsentCheck>
                <input
                  type="checkbox"
                  checked={acceptedElectronic}
                  onChange={(event) => setAcceptedElectronic(event.target.checked)}
                />
                <span>Je consens à utiliser une acceptation électronique liée à mon compte LePetitBoulot.</span>
              </ConsentCheck>
              <SignatureField>
                <label htmlFor="contract-signature-name">Saisissez votre prénom et votre nom</label>
                <input
                  id="contract-signature-name"
                  value={typedName}
                  onChange={(event) => setTypedName(event.target.value)}
                  placeholder={expectedName || 'Prénom Nom'}
                  autoComplete="name"
                />
                <small>Nom attendu : {expectedName || 'profil client incomplet'}</small>
              </SignatureField>
              <EvidenceNote>
                Il s’agit d’une acceptation électronique simple, et non d’une signature électronique qualifiée.
              </EvidenceNote>
              {validationError && <ErrorMessage role="alert">{validationError}</ErrorMessage>}
            </ConsentPanel>
          )}

          <Actions>
            {waitingForRevision ? (
              <SecondaryButton type="button" onClick={close}>Fermer</SecondaryButton>
            ) : mode === 'modify' ? (
              <>
                <SecondaryButton type="button" disabled={busy} onClick={() => setMode('review')}>
                  Retour
                </SecondaryButton>
                <PrimaryButton type="button" disabled={busy} onClick={() => runDecision('modify')}>
                  {submitting === 'modify' ? 'Envoi…' : 'Envoyer la demande'}
                </PrimaryButton>
              </>
            ) : (
              <>
                <DangerButton type="button" disabled={busy} onClick={() => runDecision('reject')}>
                  {submitting === 'reject' ? 'Refus…' : 'Refuser le contrat'}
                </DangerButton>
                <SecondaryButton type="button" disabled={busy} onClick={() => setMode('modify')}>
                  Demander une modification
                </SecondaryButton>
                <PrimaryButton type="button" disabled={busy || !signatureReady} onClick={() => runDecision('accept')}>
                  {submitting === 'accept' ? 'Signature…' : 'Accepter et signer électroniquement'}
                </PrimaryButton>
              </>
            )}
          </Actions>
        </>
      )}
    </Modal>
  );
};

const Header = styled.header`
  display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;margin-bottom:1.4rem;min-width:0;
  > div{min-width:0;}
  @media(max-width:640px){position:sticky;top:0;z-index:3;margin:-1rem -1rem 1rem;padding:1rem;border-bottom:1px solid #eee7dc;background:rgba(255,255,255,.97);backdrop-filter:blur(12px);}
`;
const Eyebrow = styled.div`
  margin-bottom: 0.35rem;
  color: #a88a5d;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;
const Title = styled.h2`
  margin: 0;
  color: #111111;
  font-size: clamp(1.35rem, 4vw, 1.9rem);
`;
const CloseButton = styled.button`
  width: 2.5rem;
  height: 2.5rem;
  flex: 0 0 auto;
  border: 0;
  border-radius: 50%;
  background: #f1ece5;
  color: #111111;
  font: inherit;
  font-size: 1.6rem;
  cursor: pointer;
`;
const ArtisanButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1rem;
  border: 1px solid #e5e1da;
  border-radius: 1rem;
  background: #faf8f4;
  color: #111111;
  font: inherit;
  text-align: left;
  cursor: pointer;

  div{display:grid;gap:.2rem;min-width:0;}
  strong,span{overflow-wrap:anywhere;}
  span{color:#726b61;font-size:.88rem;}
  &:hover { border-color: #c0a375; background: #fbf7f1; }
`;
const Avatar = styled.img`
  width: 3.4rem;
  height: 3.4rem;
  border-radius: 50%;
  object-fit: cover;
  background: #efede8;
`;
const Details = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  margin-top: 1.1rem;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
const Detail = styled.div`
  grid-column: ${({ $wide }) => ($wide ? '1 / -1' : 'auto')};
  padding: 1rem;
  border: 1px solid ${({ $highlight }) => ($highlight ? '#edd28f' : 'transparent')};
  border-radius: 0.9rem;
  background: ${({ $highlight }) => ($highlight ? '#fff9ea' : '#faf8f4')};
  color: #111111;

  span {
    display: block;
    margin-bottom: 0.4rem;
    color: #726b61;
    font-size: 0.76rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }
  p{margin:0;white-space:pre-wrap;line-height:1.6;overflow-wrap:anywhere;}
  strong{overflow-wrap:anywhere;}
  small{display:block;margin-top:.28rem;color:#7b746b;font-size:.72rem;}
`;
const MaterialSection = styled.section`
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid #e5e1da;
  border-radius: 1rem;
  h3 { margin: 0 0 0.7rem; color: #111111; font-size: 1rem; }
`;
const MaterialList = styled.div`display: grid; gap: 0.65rem;`;
const MaterialRow = styled.div`
  display:flex;align-items:center;justify-content:space-between;gap:1rem;padding-bottom:.65rem;border-bottom:1px solid #edf1f3;color:#111;min-width:0;
  &:last-child{padding-bottom:0;border-bottom:0;}
  div{display:grid;gap:.1rem;min-width:0;} strong,span{overflow-wrap:anywhere;} span{color:#72808a;font-size:.84rem;}
  @media(max-width:430px){display:grid;grid-template-columns:1fr;gap:.35rem;}
`;
const Total = styled.div`
  display:flex;align-items:center;justify-content:space-between;gap:1rem;margin-top:1rem;padding:1rem 1.1rem;border-radius:1rem;background:#111;color:#fff;
  strong{color:#c0a375;font-size:1.25rem;overflow-wrap:anywhere;}
  @media(max-width:430px){display:grid;grid-template-columns:1fr;gap:.25rem;}
`;
const DocumentAccess = styled.section`
  display:flex;align-items:center;justify-content:space-between;gap:1rem;margin-top:1rem;padding:.9rem 1rem;border:1px solid #ded5c8;border-radius:.9rem;background:#fff;
  div{display:grid;gap:.15rem;min-width:0;}strong{font-size:.88rem;}span{color:#726b61;font-size:.76rem;line-height:1.45;}
  @media(max-width:520px){display:grid;grid-template-columns:1fr;}
`;
const DocumentButton = styled.button`
  min-height:2.65rem;flex:0 0 auto;padding:.65rem .9rem;border:1px solid #111;border-radius:999px;background:#fff;color:#111;font:inherit;font-size:.78rem;font-weight:850;cursor:pointer;
  &:hover{background:#111;color:#fff;}@media(max-width:520px){width:100%;}
`;
const Notice = styled.p`
  margin: 1rem 0 0;
  padding: 0.9rem 1rem;
  border-radius: 0.85rem;
  background: #f4ede1;
  color: #8a6f48;
  line-height: 1.55;
`;

const ConsentPanel = styled.section`
  display:grid;gap:.8rem;margin-top:1rem;padding:1rem;border:1px solid #d9cfbf;border-radius:1rem;background:#fffaf1;
`;
const ConsentIntro = styled.div`display:grid;gap:.25rem;strong{font-size:.92rem;}span{color:#655d52;font-size:.8rem;line-height:1.55;}`;
const ConsentCheck = styled.label`
  display:grid;grid-template-columns:auto minmax(0,1fr);align-items:start;gap:.65rem;padding:.75rem;border:1px solid #e5dccf;border-radius:.8rem;background:#fff;cursor:pointer;
  input{width:1.15rem;height:1.15rem;margin:.05rem 0 0;accent-color:#111;}span{font-size:.8rem;line-height:1.5;}
`;
const SignatureField = styled.div`
  display:grid;gap:.35rem;label{font-size:.78rem;font-weight:850;}input{min-height:2.8rem;padding:.65rem .8rem;border:1px solid #d8cdbd;border-radius:.75rem;background:#fff;font:inherit;}small{color:#756c60;font-size:.68rem;}
`;
const EvidenceNote = styled.p`margin:0;color:#756c60;font-size:.7rem;line-height:1.5;`;

const ModificationPanel = styled.div`
  position: relative;
  display: grid;
  gap: 0.5rem;
  margin-top: 1rem;

  label { color: #222222; font-weight: 800; }
  textarea {
    min-height: 8rem;
    width: 100%;
    resize: vertical;
    padding: 0.85rem;
    border: 1px solid #ddd3c4;
    border-radius: 0.8rem;
    color:#111;
    box-sizing:border-box;
    font:inherit;
    font-size:1rem;
    line-height:1.5;
  }
`;
const Counter = styled.span`justify-self: end; color: #7a8790; font-size: 0.78rem;`;
const ErrorMessage = styled.p`margin: 0; color: #a52e27; font-size: 0.88rem;`;
const EmptyState = styled.p`
  margin: 0;
  padding: 1rem;
  border-radius: 0.8rem;
  background: #faf8f4;
  color: #66615b;
`;
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.7rem;
  margin-top: 1.4rem;

  @media(max-width:620px){display:grid;grid-template-columns:1fr;}
`;
const BaseButton = styled.button`
  min-height: 2.8rem;
  padding: 0.72rem 1rem;
  border-radius: 999px;
  font: inherit;
  font-weight: 800;
  cursor: pointer;
  box-sizing:border-box;
  &:disabled{opacity:.55;cursor:wait;}
  @media(max-width:620px){width:100%;}
`;
const PrimaryButton = styled(BaseButton)`border: 1px solid #c0a375; background: #c0a375; color: #111111;`;
const SecondaryButton = styled(BaseButton)`border: 1px solid #e7dfd2; background: #fff; color: #111111;`;
const DangerButton = styled(BaseButton)`border: 1px solid #ead2d0; background: #fff7f6; color: #9a2f29;`;

const mapStateToProps = ({ demands }) => ({ loading: demands?.loading });
const mapDispatchToProps = (dispatch) => ({
  acceptContract: (demand, consent) => dispatch(acceptContractClient(demand, consent)),
  cancelContract: (demand) => dispatch(rejectContractClient(demand)),
  modifyContract: (demand) => dispatch(askModificationClient(demand)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContractModalClient);
